import React from 'react';
import { useTranslation } from 'react-i18next';
import { EvaluationValueBadge } from '@/components/EvaluationValueBadge';
import { ChevronRight } from '@/components/Icons/ChevronRight';
import {
  CommentChip,
  RatingChip,
  ScaleChip,
} from '@/components/Icons/MarkChipIcons';
import { EvaluationChart } from '@/components/LessonDetailsModal/EvaluationChart';
import { EvaluationChartPaywall } from '@/components/LessonDetailsModal/EvaluationChartPaywall';
import { CARD_SURFACE } from '@/components/LessonDetailsModal/classNames';
import { useHasFamilySubscription } from '@/hooks/useHasFamilySubscription';
import { useShowSubscriptionOffer } from '@/hooks/useShowSubscriptionOffer';
import type { EvaluationDetail } from '@/types/overview';
import { formatDateTime } from '@/utils/date';
import { joinNonEmpty } from '@/utils/text';
import { tv } from '@/utils/tv';

const innerCard = tv({
  base: `flex min-w-0 flex-1 gap-3 ${CARD_SURFACE}`,
  variants: {
    layout: {
      mark: 'flex-col items-start px-4 py-3 md:h-[52px] md:flex-row md:items-center',
      action:
        'cursor-pointer items-center px-4 py-3 text-left transition-colors hover:bg-hover',
      note: 'flex-col items-start gap-2 p-4 md:min-h-16 md:flex-row md:items-center md:gap-3 md:px-4 md:py-3',
    },
  },
});

const NON_GRADE_VALUES: ReadonlySet<string> = new Set(['n', 'atb', 'nv', 'm']);

const MOBILE_CARD_CLASS =
  'max-md:rounded-xl max-md:bg-card max-md:shadow-card max-md:contrast:border max-md:contrast:border-border';

const DESKTOP_CARD_CLASS =
  'md:rounded-xl md:bg-card md:p-4 md:shadow-card md:contrast:border md:contrast:border-border';

interface Props {
  detail: EvaluationDetail;
  onOpenTestReport: (testId: number) => void;
}

export const EvaluationCard: React.FC<Props> = ({
  detail,
  onOpenTestReport,
}) => {
  const { t } = useTranslation();
  const hasSubscription = useHasFamilySubscription();
  const showSubscriptionOffer = useShowSubscriptionOffer();

  const { evaluation, lesson, statistics } = detail;
  const comment = evaluation.achievementRecordText || evaluation.testNotes;
  const { testId } = lesson;
  const studentPlace = statistics?.studentPlace;
  const hasChart = !!statistics && !!studentPlace;
  const hasChartBlock = hasChart && (hasSubscription || showSubscriptionOffer);
  const hasTestReport = !!testId && !NON_GRADE_VALUES.has(evaluation.value);

  return (
    <div
      className={`flex flex-col gap-2 md:gap-8 ${hasChartBlock ? DESKTOP_CARD_CLASS : ''}`}
    >
      <div className="flex flex-col gap-2 md:gap-3">
        <div className="flex flex-col gap-2 md:flex-row md:gap-3">
          <div className={innerCard({ layout: 'mark' })}>
            <EvaluationValueBadge
              value={evaluation.value}
              valueFullName={evaluation.valueFullName}
              lessonTypeId={lesson.type.lessonTypeId}
              nonAttendanceType={evaluation.nonAttendanceType}
            />
            <span className="text-body-13 flex flex-col gap-0.5 text-muted-foreground md:hidden">
              <span>
                {t('diary.evaluation.author', { value: evaluation.authorName })}
              </span>
              <span>{formatDateTime(evaluation.timeCreated)}</span>
            </span>
            <span className="text-body-14 hidden min-w-0 flex-1 truncate text-muted-foreground md:block">
              {t('diary.evaluation.author', {
                value: joinNonEmpty([
                  evaluation.authorName,
                  formatDateTime(evaluation.timeCreated),
                ]),
              })}
            </span>
          </div>

          {hasTestReport && (
            <button
              type="button"
              onClick={() => onOpenTestReport(testId)}
              className={innerCard({ layout: 'action' })}
            >
              <RatingChip />
              <span className="text-subhead-14 md:text-body-14 min-w-0 flex-1 truncate text-foreground">
                {t('diary.evaluation.testOverview')}
              </span>
              <ChevronRight className="size-4 shrink-0 text-foreground" />
            </button>
          )}
        </div>

        {!!comment && (
          <div
            className={innerCard({
              layout: 'note',
              className: 'md:order-last',
            })}
          >
            <CommentChip />
            <p className="text-body-13 md:text-body-14 min-w-0 flex-1 text-muted-foreground">
              {comment}
            </p>
          </div>
        )}

        {!!evaluation.evaluationWeight && (
          <div className={innerCard({ layout: 'note' })}>
            <span className="flex shrink-0 items-center gap-2">
              <ScaleChip />
              <span className="text-subhead-14 text-foreground">
                {evaluation.evaluationWeight}
              </span>
            </span>
            <p className="text-body-13 md:text-body-14 min-w-0 flex-1 text-muted-foreground">
              {t('diary.evaluation.weighted', {
                weight: evaluation.evaluationWeight,
              })}
            </p>
          </div>
        )}
      </div>

      {hasChart && hasSubscription && (
        <div className={`max-md:p-4 ${MOBILE_CARD_CLASS}`}>
          <EvaluationChart
            statistics={statistics}
            studentPlace={studentPlace}
          />
        </div>
      )}

      {hasChart && showSubscriptionOffer && (
        <div className={`max-md:px-4 max-md:py-6 ${MOBILE_CARD_CLASS}`}>
          <EvaluationChartPaywall />
        </div>
      )}
    </div>
  );
};
